import type { Lead, LeadInteractionType } from '@/types';
import i18n from '@/i18n/config';

import React from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Textarea, Select, SelectItem } from '@heroui/react';

interface AddInteractionModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  lead: Lead | null;
  isSubmitting: boolean;
  onSubmit: (type: LeadInteractionType, note: string, outcome?: string | null) => void;
}

const INTERACTION_TYPES: Array<{ key: LeadInteractionType; label: string }> = [
  { key: 'call', label: i18n.t('copy.call') },
  { key: 'email', label: i18n.t('auth.email') },
  { key: 'meeting', label: i18n.t('copy.meeting') },
  { key: 'whatsapp', label: i18n.t('copy.whatsapp') }
];

export function AddInteractionModal({ isOpen, onOpenChange, lead, isSubmitting, onSubmit }: AddInteractionModalProps) {
  const [type, setType] = React.useState<LeadInteractionType>('call');
  const [note, setNote] = React.useState('');
  const [outcome, setOutcome] = React.useState('');

  React.useEffect(() => {
    if (!isOpen) {
      setType('call');
      setNote('');
      setOutcome('');
    }
  }, [isOpen]);

  const handleSubmit = React.useCallback(() => {
    if (!note.trim()) {
      return;
    }

    onSubmit(type, note.trim(), outcome.trim() || null);
    setType('call');
    setNote('');
    setOutcome('');
  }, [type, note, outcome, onSubmit]);

  const handleKeyPress = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  if (!lead) return null;

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={onOpenChange} size="md">
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">Add Interaction for {lead.domain}</ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4" onKeyDown={handleKeyPress}>
                <Select
                  label={i18n.t('filters.type')}
                  selectedKeys={new Set([type])}
                  onSelectionChange={(keys) => {
                    const selectedType = Array.from(keys)[0] as LeadInteractionType;
                    if (selectedType) {
                      setType(selectedType);
                    }
                  }}
                  isRequired
                >
                  {INTERACTION_TYPES.map((item) => (
                    <SelectItem key={item.key}>{item.label}</SelectItem>
                  ))}
                </Select>

                <Textarea
                  label={i18n.t('copy.note')}
                  placeholder={i18n.t('copy.enter_interaction_details')}
                  value={note}
                  onValueChange={setNote}
                  minRows={4}
                  isRequired
                />

                <Textarea
                  label={i18n.t('copy.outcome_optional')}
                  placeholder={i18n.t('copy.enter_outcome_if_any')}
                  value={outcome}
                  onValueChange={setOutcome}
                  minRows={2}
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="danger" variant="light" onPress={onClose} isDisabled={isSubmitting}>{i18n.t('modals.exportConfirm.cancel')}</Button>
              <Button color="primary" onPress={handleSubmit} isLoading={isSubmitting} isDisabled={!note.trim()}>
                Add Interaction
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}

